import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ──────────────────────────────────────────────────────────────────────
// A/B test: lead-submit button copy
//
// Hypothesis: shorter / urgency-flavored copy ("Get matched now (free)")
// lifts the Angi affiliate redirect rate vs the current control
// ("Match me with contractors").
//
// Bucket: stable per-browser via localStorage so a single user sees the
// same variant across sessions. Exposure event fires once per dialog
// open so we can compute CTR = redirects ÷ exposures per variant in
// /admin/analytics/affiliate-revenue.
// ──────────────────────────────────────────────────────────────────────
const AB_STORAGE_KEY = "havencost_ab_lead_submit_variant";
const AB_VARIANTS = {
  A: "Match me with contractors",
  B: "Get matched now (free)",
};

function getOrAssignVariant() {
  if (typeof window === "undefined") return "A";
  try {
    const existing = window.localStorage.getItem(AB_STORAGE_KEY);
    if (existing === "A" || existing === "B") return existing;
    const v = Math.random() < 0.5 ? "A" : "B";
    window.localStorage.setItem(AB_STORAGE_KEY, v);
    return v;
  } catch {
    return "A";
  }
}

export default function LeadDialog({ open, onOpenChange, source = "site", projectType = "", angiUrl = null }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [project, setProject] = useState(projectType);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [redirectedToAngi, setRedirectedToAngi] = useState(false);
  // 2026-09-16 pass 92j — Inline validation state. Admin analytics
  // showed 0/41 lead-submit conversion over 90d — root cause was the
  // browser's native `required` tooltip being invisible/ignored on
  // mobile. Replaced with per-field inline errors that stay visible
  // and log a `lead_submit_attempted_with_errors` event so the funnel
  // widget can measure friction going forward.
  const [errors, setErrors] = useState({});

  // Assign + remember the A/B variant. Computed lazily once per mount so
  // every render in the same dialog session uses the same bucket.
  const variant = useMemo(() => getOrAssignVariant(), []);
  const submitLabel = AB_VARIANTS[variant] || AB_VARIANTS.A;

  // Fire the exposure event the first time the dialog actually opens, so
  // we don't inflate counts on hidden re-renders.
  useEffect(() => {
    if (!open) return;
    // Funnel top-of-funnel event — paired with `lead_submit` at the
    // bottom to compute completion rate per source in the admin widget.
    track("lead_dialog_opened", { source, variant });
    track("ab_lead_submit_variant_exposed", { variant, source });
  }, [open, variant, source]);

  const reset = () => {
    setName(""); setEmail(""); setZip(""); setProject(projectType); setDone(false); setRedirectedToAngi(false); setErrors({});
  };

  // Basic email shape check — pragmatic, not RFC-compliant. Rejects
  // "foo", "foo@", "foo@bar" while accepting "a@b.c". Anything more
  // strict rejects real edge cases (e.g. tag+alias@sub.tld).
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Please enter your full name.";
    if (!email.trim()) e.email = "Please enter your email so contractors can reach you.";
    else if (!EMAIL_RE.test(email.trim())) e.email = "That email doesn't look right. Try again?";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const eMap = validate();
    if (Object.keys(eMap).length > 0) {
      setErrors(eMap);
      // Log a friction event so /admin/analytics/affiliate-revenue can
      // measure how many opens are dying at validation rather than at
      // the actual submit — a 0/41 conversion rate hides this signal.
      track("lead_submit_attempted_with_errors", {
        source,
        variant,
        missing: Object.keys(eMap),
      });
      // Focus the first invalid field so mobile users don't need to
      // hunt for the error state.
      const firstErrKey = Object.keys(eMap)[0];
      const el = document.querySelector(`[data-testid="lead-input-${firstErrKey}"]`);
      if (el) {
        el.focus();
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      return;
    }
    setErrors({});
    setSubmitting(true);
    // 2026-10-01 pass 92x — Open the affiliate tab SYNCHRONOUSLY inside the
    // submit click. Previously window.open ran after `await axios.post`,
    // which breaks the browser's user-activation chain — popup blockers
    // killed the tab, our tracker still logged a "redirect", but the browser
    // never reached CJ's click domain (why admin clicks weren't showing up
    // in CJ reports). If the lead POST fails we close the pre-opened tab.
    let angiWin = null;
    if (angiUrl) {
      angiWin = window.open(angiUrl, "_blank", "noopener,noreferrer");
    }
    try {
      await axios.post(`${API}/leads`, {
        name,
        email,
        zip_code: zip || null,
        project_type: project || null,
        source,
      });
      track("lead_submit", { source, project_type: project || null, variant });
      // Also fire GA4's recommended standard event `generate_lead`. Same
      // action, standardized name — unlocks GA4's built-in Lead-generation
      // report widget and Advertising → Attribution reporting without
      // requiring anyone to mark it as a Key event manually. GA4 auto-
      // recognizes this event name and populates the funnel widgets.
      // Reference: https://support.google.com/analytics/answer/9267735
      track("generate_lead", {
        currency: "USD",
        value: 15, // avg est. lead value; adjust after 30d attribution
        source,
        project_type: project || null,
        variant,
      });
      // After capturing the lead, confirm the affiliate tab actually opened
      // (it was pre-opened synchronously at submit-click time — see pass 92x
      // note above). `blocked` tells the admin widget how many redirects a
      // popup blocker killed before the browser reached CJ.
      if (angiUrl) {
        const ok = !!angiWin && !angiWin.closed;
        setRedirectedToAngi(ok);
        track("angi_affiliate_redirect", { source, variant, blocked: !ok });
      }
      setDone(true);
      toast.success("Thanks! We'll match you with up to 3 local contractors.");
    } catch (err) {
      if (angiWin) {
        try { angiWin.close(); } catch { /* ignore */ }
      }
      toast.error("Couldn't submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (next) => {
    if (!next) setTimeout(reset, 200);
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="lead-dialog">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Get free quotes from local contractors</DialogTitle>
              <DialogDescription className="text-slate-600">
                Tell us about your project and we'll match you with up to 3 vetted local
                pros — no obligation, no spam.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} noValidate className="mt-2 space-y-4">
              <div className="grid gap-1.5">
                <Label htmlFor="lead-name">Full name</Label>
                <Input
                  id="lead-name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  placeholder="Jane Smith"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? "lead-name-error" : undefined}
                  className={errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}
                  data-testid="lead-input-name"
                />
                {errors.name && (
                  <p id="lead-name-error" className="text-xs font-medium text-red-600" data-testid="lead-input-name-error">
                    {errors.name}
                  </p>
                )}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="lead-email">Email</Label>
                <Input
                  id="lead-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  placeholder="jane@example.com"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "lead-email-error" : undefined}
                  className={errors.email ? "border-red-500 focus-visible:ring-red-500" : ""}
                  data-testid="lead-input-email"
                />
                {errors.email && (
                  <p id="lead-email-error" className="text-xs font-medium text-red-600" data-testid="lead-input-email-error">
                    {errors.email}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="lead-zip">ZIP code</Label>
                  <Input
                    id="lead-zip"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    placeholder="94110"
                    maxLength={10}
                    data-testid="lead-input-zip"
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="lead-project">Project</Label>
                  <Input
                    id="lead-project"
                    value={project}
                    onChange={(e) => setProject(e.target.value)}
                    placeholder="Bathroom remodel"
                    data-testid="lead-input-project"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-green-600 text-white hover:bg-green-700"
                data-testid="lead-submit-button"
                data-variant={variant}
              >
                {submitting ? "Submitting…" : submitLabel}
              </Button>

              <p className="flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
                Your info is shared only with contractors you choose to work with.
              </p>
              {angiUrl && (
                <p className="text-xs text-slate-400">
                  After submitting, we'll also open Angi in a new tab to show you pre-screened
                  pros in your area. We may earn a referral fee if you hire through Angi.
                </p>
              )}
            </form>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="lead-success">
            <CheckCircle2 className="h-12 w-12 text-green-600" />
            <h3 className="font-display text-xl font-semibold text-slate-900">You're all set</h3>
            {angiUrl ? (
              <>
                <p className="max-w-xs text-sm text-slate-600">
                  We've got your details. {redirectedToAngi
                    ? "We've also opened Angi in a new tab so you can see pre-screened pros in your area right now."
                    : "Click below to also see pre-screened Angi pros in your area right now."}
                </p>
                {!redirectedToAngi && (
                  <a
                    href={angiUrl}
                    target="_blank"
                    rel="sponsored nofollow noopener noreferrer"
                    onClick={() => track("angi_affiliate_redirect_manual", { source })}
                    className="mt-2 inline-flex items-center gap-2 rounded-full bg-amber-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-amber-700"
                    data-testid="lead-success-angi-fallback"
                  >
                    See pre-screened pros on Angi →
                  </a>
                )}
                <p className="mt-1 text-xs text-slate-400">
                  We may earn a referral fee if you hire through Angi — no cost to you.
                </p>
              </>
            ) : (
              <p className="max-w-xs text-sm text-slate-600">
                We've received your request. We'll connect you with vetted local contractors in your area — you'll hear from them soon.
              </p>
            )}
            <Button onClick={() => handleOpenChange(false)} className="mt-2 rounded-full" variant="outline">
              Close
            </Button>
            {/* Passive recruitment: every successful homeowner submission becomes
                an opportunity to seed the contractor pipeline before the lead-
                forwarding network goes live. Discreet, single-line, no upsell. */}
            <p className="mt-4 border-t border-slate-100 pt-4 text-xs text-slate-500">
              Are you a contractor?{" "}
              <a
                href="/for-contractors"
                className="font-medium text-green-700 hover:text-green-800 hover:underline"
                data-testid="contractor-signup-link"
              >
                Apply to receive leads in your area →
              </a>
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
